import { useState, useEffect } from "react";
import { Routes, Route, useNavigate, Navigate, useLocation } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { Toaster } from "@/components/ui/toaster";
import Auth from "@/pages/Auth";
import Index from "@/pages/Index";
import Templates from "@/pages/Templates";
import Documents from "@/pages/Documents";
import Settings from "@/pages/Settings";
import CompanyLibrary from "@/pages/CompanyLibrary";

import Upload from "@/pages/Upload";
import BulkUpload from "@/pages/BulkUpload";
import Terms from "@/pages/Terms";
import Profile from "@/pages/Profile";
import { AppLayout } from "@/components/AppLayout";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import VerificationSuccess from "@/pages/VerificationSuccess";
import PasswordReset from "@/pages/PasswordReset";
import { supabase } from "@/integrations/supabase/client";
import DocumentEditor from "@/pages/DocumentEditor";
import PlaceholderReview from "@/pages/PlaceholderReview";
import { AdminLayout } from "@/components/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import ReviewQueue from "@/pages/admin/ReviewQueue";
import ReviewDocument from "@/pages/admin/ReviewDocument";
import TemplateReviewPage from "@/pages/admin/TemplateReviewPage";
import UploadTemplate from "@/pages/UploadTemplate";
import { identifyUser, resetIdentity, trackPageview } from "@/lib/analytics";

export default function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        console.log("Checking authentication session...");
        const { data: { session } } = await supabase.auth.getSession();
        console.log("Session check result:", session ? "Authenticated" : "Not authenticated");
        setIsAuthenticated(!!session);
        
        // Identify user if session exists
        if (session?.user) {
          identifyUser(session.user.id, {
            email: session.user.email,
          });
        } else {
          resetIdentity();
        }
        
        setIsLoading(false);
      } catch (error) {
        console.error("Error checking auth session:", error);
        setIsAuthenticated(false);
        setIsLoading(false);
      }
    };

    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log("Auth state changed:", event);
      setIsAuthenticated(!!session);
      
      // Identify user on auth state changes
      if (session?.user) {
        identifyUser(session.user.id, {
          email: session.user.email,
        });
      } else {
        resetIdentity();
      }
      
      if (event === 'SIGNED_OUT') {
        toast({
          title: "Signed out",
          description: "You have been signed out of your account",
        });
        navigate("/auth");
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate, toast]);

  // Capture SPA pageviews on route change
  useEffect(() => {
    // Use pathname+search+hash to track full route
    const path = window.location.pathname + window.location.search + window.location.hash;
    trackPageview(path);
  }, [location]);

  // Show a loading indicator while checking authentication
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <>
      <Routes>
        <Route path="/auth" element={isAuthenticated ? <Navigate to="/" replace /> : <Auth />} />
        <Route path="/verification-success" element={<VerificationSuccess />} />
        <Route path="/password-reset" element={<PasswordReset />} />
        
        <Route path="/" element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
          <Route index element={<Index />} />
          <Route path="templates" element={<Templates />} />
          <Route path="templates/:id/edit" element={<DocumentEditor />} />
          <Route path="templates/:id/review-placeholders" element={<PlaceholderReview />} />
          <Route path="templates/:id" element={<DocumentEditor />} />
          <Route path="documents" element={<Documents />} />
          <Route path="documents/:id/edit" element={<DocumentEditor />} />
          <Route path="settings" element={<Settings />} />
          <Route path="library" element={<CompanyLibrary />} />

          <Route path="upload" element={<Upload />} />
          <Route path="bulk-upload" element={<BulkUpload />} />
          <Route path="terms" element={<Terms />} />
          <Route path="profile" element={<Profile />} />
          <Route path="templates/upload" element={<UploadTemplate />} />
        </Route>
        
        {/* Admin Routes */}
        <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
          <Route index element={<AdminDashboard />} />
          <Route path="review" element={<ReviewQueue />} />
          <Route path="review/:id" element={<ReviewDocument />} />
          <Route path="templates/:templateId" element={<TemplateReviewPage />} />
        </Route>
      </Routes>
      <Toaster />
    </>
  );
}
